import React from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { Download, Share2, MoreVertical, Trash2 } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { TableCell, TableRow } from 'components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from 'components/ui/tooltip'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import DocumentAvatar from './DocumentAvatar'
import {
	getCurrentVersion,
	type LibraryDocument,
	type UserSummary,
} from '../types/documentLibrary.types'
import { sourceLabel } from '../utils/sourceLabel'

interface DocumentRowProps {
	document: LibraryDocument
	onPreview: (id: string) => void
	onShowVersions: (id: string) => void
	onShare: (id: string, versionId?: string) => void
	onRowClick: (id: string) => void
	onSoftDelete?: (id: string) => void
	/**
	 * When true, render a Source cell between File Name and Owner showing
	 * the doc's parent (Project / Portfolio / Location / Global). Driven
	 * by `DocumentLibraryList` for the my-library cross-scope view.
	 */
	showSource?: boolean
}

const ownerLabel = (owner: LibraryDocument['owner']): string => {
	if (!owner) return ''
	if (typeof owner === 'string') return ''
	const u = owner as UserSummary
	const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
	return full || u.email || ''
}

/** Initials for the owner avatar when the user has no photo set. */
const ownerInitials = (owner: LibraryDocument['owner']): string => {
	if (!owner || typeof owner === 'string') return ''
	const u = owner as UserSummary
	const fromName = [u.firstName, u.lastName]
		.filter(Boolean)
		.map(part => (part as string).charAt(0))
		.join('')
	return (fromName || u.email?.charAt(0) || '').toUpperCase()
}

const ownerImage = (owner: LibraryDocument['owner']): string | undefined =>
	!owner || typeof owner === 'string' ? undefined : (owner as UserSummary).image

const formatDate = (iso: string | null | undefined): string =>
	iso ? moment(iso).format('MM/DD/YY') : ''

const stop = (e: React.MouseEvent) => e.stopPropagation()

const DocumentRow: React.FC<DocumentRowProps> = ({
	document,
	onPreview,
	onShowVersions,
	onShare,
	onRowClick,
	onSoftDelete,
	showSource = false,
}) => {
	const currentVersion = getCurrentVersion(document)
	const filename = currentVersion?.originalFilename || document.libraryName

	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const currentUserId = userInfo?._id
	const ownerId =
		typeof document.owner === 'string'
			? document.owner
			: (document.owner as UserSummary)?._id
	const isOwner =
		currentUserId && ownerId && String(currentUserId) === String(ownerId)

	const ownerName = ownerLabel(document.owner)

	return (
		<TableRow
			className="h-12 hover:bg-[#fffbf2] cursor-pointer"
			onClick={() => onRowClick(document._id)}
		>
			<TableCell className="py-2">
				<DocumentAvatar
					filename={filename}
					size={36}
					onClick={() => onPreview(document._id)}
					thumbnailUrl={currentVersion?.thumbnailUrl}
				/>
			</TableCell>
			<TableCell className="py-2 text-sm">
				<TooltipProvider delayDuration={300}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={e => {
									e.stopPropagation()
									onPreview(document._id)
								}}
								className="text-[#0080fe] hover:underline truncate max-w-full text-left bg-transparent border-0 p-0 cursor-pointer"
								style={{ display: 'block' }}
							>
								{filename}
							</button>
						</TooltipTrigger>
						<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-[11px]">
							Click to preview.
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
				{document.libraryName !== currentVersion?.originalFilename && (
					<div className="text-xs text-gray-500 truncate">
						{document.libraryName}
					</div>
				)}
			</TableCell>
			{showSource && (
				<TableCell className="py-2 text-sm truncate" title={sourceLabel(document)}>
					{sourceLabel(document)}
				</TableCell>
			)}
			<TableCell className="py-2 text-sm whitespace-nowrap">
				{/* No avatar on an unpopulated owner: a lone grey circle beside a
				    blank name reads as a broken image rather than as no data. */}
				{ownerName ? (
					<span className="flex items-center gap-1.5">
						<Avatar className="h-5 w-5 shrink-0">
							<AvatarImage src={ownerImage(document.owner)} alt="" />
							<AvatarFallback className="text-[9px] bg-gray-200 text-gray-600">
								{ownerInitials(document.owner)}
							</AvatarFallback>
						</Avatar>
						<span className="truncate">{ownerName}</span>
					</span>
				) : null}
			</TableCell>
			<TableCell className="py-2 text-sm whitespace-nowrap">
				{formatDate(currentVersion?.uploadedAt || document.updatedAt)}
			</TableCell>
			<TableCell className="py-2 text-sm whitespace-nowrap">
				{formatDate(currentVersion?.fileTrueCreatedAt)}
			</TableCell>
			<TableCell className="py-2 text-sm text-center">
				v{currentVersion?.versionNumber ?? '?'}
			</TableCell>
			<TableCell className="py-2 text-sm text-center" onClick={stop}>
				<TooltipProvider delayDuration={300}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={() =>
									onShare(document._id, currentVersion?._id)
								}
								className="text-gray-500 hover:text-[#0080fe] transition-colors"
								aria-label="Share document"
							>
								<Share2 className="w-4 h-4" />
							</button>
						</TooltipTrigger>
						<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-[11px]">
							Share document
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</TableCell>
			<TableCell className="py-2 text-sm text-center" onClick={stop}>
				<TooltipProvider delayDuration={300}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={() => onShowVersions(document._id)}
								disabled={!currentVersion}
								className="text-gray-500 hover:text-[#0080fe] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
								aria-label="Download document (opens version history)"
							>
								<Download className="w-4 h-4" />
							</button>
						</TooltipTrigger>
						<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-[11px]">
							Download (choose version)
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</TableCell>
			{isOwner && onSoftDelete && (
				<TableCell className="py-2 text-sm text-center" onClick={stop}>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="text-gray-500 hover:text-[#0080fe] transition-colors"
								aria-label="More actions"
							>
								<MoreVertical className="w-4 h-4" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() => onSoftDelete(document._id)}
								className="text-red-600 focus:text-red-600"
							>
								<Trash2 className="w-4 h-4 mr-2" />
								Delete document
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</TableCell>
			)}
		</TableRow>
	)
}

export default DocumentRow
